import { useState, useEffect, useCallback } from 'react';
import { api, MarketplaceArche } from '@/lib/api';

interface UseSuggestedArchesResult {
  arches: MarketplaceArche[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSuggestedArches(currentArcheId: string): UseSuggestedArchesResult {
  const [arches, setArches] = useState<MarketplaceArche[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSuggestions = useCallback(async () => {
    if (!currentArcheId) {
      setArches([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Try metadata-driven suggestions
      try {
        const metadata = await api.getArcheMetadata(currentArcheId);
        if (metadata?.suggestedArches?.length) {
          const result = await api.getFeaturedArches(20);
          const suggestions = metadata.suggestedArches
            .map((id) => result.arches.find((a) => a.archId === id))
            .filter((a): a is MarketplaceArche => a !== undefined);
          if (suggestions.length > 0) {
            setArches(suggestions);
            return;
          }
        }
      } catch {
        // Metadata fetch failed, continue to fallback
      }

      // 2. Try to get current arche details for category/tag matching
      let currentArche: { categorySlug?: string | null; tags?: string[] } | null = null;
      try {
        currentArche = await api.getMarketplaceArche(currentArcheId);
      } catch {
        // Arche might not exist in marketplace yet
      }

      // If current arche has a category, show same-category arches
      if (currentArche?.categorySlug) {
        try {
          const categoryResult = await api.getMarketplaceCategoryArches(currentArche.categorySlug, {
            sortBy: 'fitness',
            limit: 10,
          });
          const suggestions = categoryResult.arches.filter((a) => a.archId !== currentArcheId);
          if (suggestions.length > 0) {
            setArches(suggestions);
            return;
          }
        } catch {
          // Category fetch failed, continue to fallback
        }
      }

      // If current arche has tags, search by tags
      if (currentArche?.tags && currentArche.tags.length > 0) {
        try {
          const searchResult = await api.searchMarketplace({
            tags: currentArche.tags.slice(0, 3),
            sortBy: 'fitness',
            limit: 10,
          });
          const suggestions = searchResult.arches.filter((a) => a.archId !== currentArcheId);
          if (suggestions.length > 0) {
            setArches(suggestions);
            return;
          }
        } catch {
          // Tag search failed, continue to fallback
        }
      }

      // 3. Fallback: show featured arches (excluding current)
      const result = await api.getFeaturedArches(20);
      const suggestions = result.arches.filter((a) => a.archId !== currentArcheId).slice(0, 10);
      setArches(suggestions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suggestions');
      setArches([]);
    } finally {
      setLoading(false);
    }
  }, [currentArcheId]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  return {
    arches,
    loading,
    error,
    refetch: fetchSuggestions,
  };
}

export default useSuggestedArches;
